import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { getProductSlug, SITE_URL } from "@/utils/seo";
import { API, BACKEND_URL } from "@/config";
import { ArrowRight, Users, User, UserPlus, UserCheck, Package } from "lucide-react";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Stroller Rentals", item: `${SITE}/strollers` },
  ],
};

const imgSrc = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

/** Returns the filter key for a product/bundle */
const itemTypeKey = (item) => {
  const sub = (item.subcategory || "").toLowerCase().trim();

  // "Extras" subcategory always routes to the extras tab — for both products and bundles
  if (sub === "extras") return "extras";

  // Bundles: use the Admin-configured subcategory field first
  if (item.is_bundle) {
    if (sub === "trio" || sub === "triple" || sub === "triples") return "triple";
    if (sub === "quad" || sub === "quads") return "quad";
    if (sub === "double" || sub === "doubles" || sub === "twin") return "double";
    if (sub === "single" || sub === "singles") return "single";
    // no recognized subcategory — fall through to stroller_attributes
  }
  // Regular products (and bundles with unset subcategory): use stroller_attributes
  const sa = item.stroller_attributes || {};
  const t = (sa.stroller_type || sa.seating_style || "").toLowerCase();
  if (t === "double" || t === "twin") return "double";
  if (t === "triple") return "triple";
  if (t === "quad") return "quad";
  return "single";
};

const BADGE_LABELS = {
  single: "Single",
  double: "Double",
  triple: "Triple",
  quad: "Quad",
  extras: "Extras",
};

export default function Strollers() {
  const [items, setItems] = useState([]);   // strollers + bundles (deduplicated)
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/bundles`).catch(() => ({ data: { bundles: [] } })),
    ]).then(([pRes, bRes]) => {
      const strollerProducts = (pRes.data.products || []).filter(
        (p) => p && p.category === "strollers" && p.website_visible !== false && p.active !== false && !p.addon_only
      );
      const strollerBundles = (bRes.data.bundles || []).filter(
        (b) => b && (b.category === "strollers" || b.category === "bundles") &&
          b.website_visible !== false && b.active !== false
      ).map((b) => ({ ...b, is_bundle: true }));

      // /api/products already merges bundles (is_bundle:true) into its response.
      // /api/bundles returns the same bundles again — deduplicate by ID so each
      // bundle card only appears once (using the dedicated bundle fetch as truth).
      const bundleIds = new Set(strollerBundles.map((b) => b.id));
      // Also hide any standalone component product that is part of a bundle.
      const bundleComponentIds = new Set(
        strollerBundles.flatMap((b) => (b.components || []).map((c) => c.product_id).filter(Boolean))
      );
      const deduped = strollerProducts.filter(
        (p) => !bundleIds.has(p.id) && !bundleComponentIds.has(p.id)
      );

      setItems([...deduped, ...strollerBundles]);
    }).finally(() => setLoading(false));
  }, []);

  // Derive counts per type key — tabs only appear when ≥1 item exists for that type
  const byType = { single: [], double: [], triple: [], quad: [], extras: [] };
  items.forEach((item) => { const k = itemTypeKey(item); if (byType[k]) byType[k].push(item); });
  const displayed = filter === "all" ? items : (byType[filter] || []);

  const TAB_DEFS = [
    { key: "all", label: "All", Icon: null },
    { key: "single", label: "Singles", Icon: User },
    { key: "double", label: "Doubles", Icon: Users },
    { key: "triple", label: "Triples", Icon: UserPlus },
    { key: "quad", label: "Quads", Icon: UserCheck },
    { key: "extras", label: "Extras", Icon: Package },
  ];
  const tabs = TAB_DEFS.filter(({ key }) => key === "all" || byType[key]?.length > 0);

  return (
    <>
      <PageSEO
        title="Stroller Rentals Near Disney World & Orlando | Enchanted Gear Rentals"
        description="Browse our full stroller rental catalog — single, double, triple and quad strollers from Baby Jogger, BOB, Nuna, UPPAbaby and Zoe. Delivered free to Disney resorts and Orlando-area hotels."
        canonical={`${SITE}/strollers`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "var(--color-bg, #040A0F)" }}>
        {/* Hero */}
        <section className="pt-28 pb-12 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Stroller Rentals</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-4">
              Stroller Rentals Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Browse our full lineup of premium single, double, triple and quad strollers — all hospital-grade sanitized between rentals and delivered free to your resort, hotel or vacation home.
            </p>
            <p className="mt-3 text-sm text-[#B9C8D5]">From <span className="text-[#FFD166] font-semibold">$20/day</span> · Free delivery · Enchanted Gear Rentals is not affiliated with The Walt Disney Company.</p>
          </div>
        </section>

        {/* Filter tabs — dynamic, only show tabs with items */}
        <div className="container pt-8 pb-4">
          <div className="flex gap-3 justify-center flex-wrap">
            {tabs.map(({ key, label, Icon }) => {
              const count = key === "all" ? items.length : byType[key]?.length || 0;
              return (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  data-testid={`strollers-filter-${key}`}
                  className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium border transition-all ${filter === key ? "bg-[#FFD166] text-[#040A0F] border-[#FFD166]" : "border-[#FFD166]/30 text-[#B9C8D5] hover:border-[#FFD166]/70"}`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {label} <span className="opacity-60 text-xs">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Product grid */}
        <section className="container pb-20 pt-4">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-[#0A1825] border border-[#FFD166]/10 animate-pulse h-80" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="strollers-grid">
              {displayed.map((p) => {
                const slug = getProductSlug(p);
                const sa = p.stroller_attributes || {};
                const img = imgSrc(p.image);
                const typeKey = itemTypeKey(p);
                const badgeLabel = BADGE_LABELS[typeKey] || "Single";
                return (
                  <Link
                    key={p.id}
                    to={`/strollers/${slug}`}
                    className="group rounded-2xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 overflow-hidden transition-all hover:-translate-y-1"
                    data-testid={`stroller-card-${p.id}`}
                  >
                    <div className="aspect-[4/3] bg-[#060f18] overflow-hidden">
                      {img ? (
                        <img
                          src={img}
                          alt={`${p.name} available for Orlando stroller rental near Walt Disney World`}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          width="400"
                          height="300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#FFD166]/20 text-5xl">&#9885;</div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full">
                          {badgeLabel}
                        </span>
                        {sa.max_child_weight_lbs ? (
                          <span className="text-[10px] text-[#B9C8D5]">up to {sa.max_child_weight_lbs} lbs</span>
                        ) : null}
                        {p.is_bundle && (
                          <span className="text-[10px] text-[#FFD166]/60 border border-[#FFD166]/15 px-1.5 py-0.5 rounded-full">Bundle</span>
                        )}
                      </div>
                      <h2 className="font-serif text-xl text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">{p.name}</h2>
                      <p className="text-sm text-[#B9C8D5] mt-1 line-clamp-2">{p.tagline || p.description?.slice(0, 80)}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-[#FFD166] font-semibold">From ${p.tier_1_3}/day</span>
                        <span className="text-xs text-[#B9C8D5] flex items-center gap-1">View details <ArrowRight className="w-3 h-3" /></span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Ready to book your stroller?</h2>
            <p className="text-[#B9C8D5] mb-6">Select your dates and delivery location to check availability and confirm pricing in real time.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="strollers-book-cta">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

